/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useState, useEffect } from "react";

export enum ScriptStatus {
  idle = "idle",
  loading = "loading",
  ready = "ready",
  error = "error",
}

const CONTAINERS_TO_REMOVE = [
  ".PensioEmbeddedPaymentWindow",
  ".PensioEmbeddedPaymentWindowBackground",
];

export const useScript = (src: string, scriptDataName = "payment-frame") => {
  // Keep track of script status ("idle", "loading", "ready", "error")
  const [status, setStatus] = useState<string>(
    src ? ScriptStatus.loading : ScriptStatus.idle
  );

  let script: HTMLScriptElement | null = null;

  useEffect(
    () => {
      // Allow falsy src value if waiting on other data needed for
      // constructing the script URL passed to this hook.
      if (!src) {
        setStatus(ScriptStatus.idle);
        return;
      }

      // Fetch existing script element by src
      // It may have been added by another intance of this hook
      script = document.querySelector(
        `[data-name="${scriptDataName}"]`
      ) as HTMLScriptElement;

      if (!script) {
        // Create script
        script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.setAttribute("data-status", ScriptStatus.loading);
        script.setAttribute("data-name", scriptDataName);
        // Add script to document body
        document.body.appendChild(script);

        // Store status in attribute on script
        // This can be read by other instances of this hook
        const setAttributeFromEvent = (event: Event) => {
          script?.setAttribute(
            "data-status",
            event.type === "load" ? "ready" : "error"
          );
        };

        script.addEventListener("load", setAttributeFromEvent);
        script.addEventListener("error", setAttributeFromEvent);
      } else {
        // Grab existing script status from attribute and set to state.
        setStatus((script.getAttribute("data-status") as string) ?? "ready");
      }

      // Script event handler to update status in state
      // Note: Even if the script already exists we still need to add
      // event handlers to update the state for *this* hook instance.
      const setStateFromEvent = (event: Event) => {
        setStatus(event.type === "load" ? "ready" : "error");
      };

      // Add event listeners
      script.addEventListener("load", setStateFromEvent);
      script.addEventListener("error", setStateFromEvent);
      return () => {
        if (script) {
          script.removeEventListener("load", setStateFromEvent);
          script.removeEventListener("error", setStateFromEvent);

          CONTAINERS_TO_REMOVE.forEach((containerClass) => {
            document.querySelectorAll(containerClass)?.forEach((el) => {
              el.remove();
            });
          });
          script.remove();
        }
      };
    },
    [src] // Only re-run effect if script src changes
  );
  return status;
};
